/**
 * Die Karte „Das ist neu“ im Chat. (17.10.2026, Entwurf A+B)
 *
 * Der kleine Chip „Neu · n“ über dem Eingabefeld ist weg (Martin 21.10.2026: Platzverschwendung). Die
 * Neuerungen erreicht man über den Knopf „Was ist neu“ im Menü (lib/was-ist-neu.ts); der Name der Datei
 * blieb, damit die Importe stehen.
 *
 *   - Die Karte legt EINE Karte in den Verlauf (eine ältere wird ersetzt). Das Öffnen merkt nichts:
 *     erst das Ansehen der Karte (sie war ihre Frist am Stück im Bild, `sichtFrist`) setzt den Merker
 *     (voice-v8.was-ist-neu.gesehen).
 * Aussehen: style.css (.neuk*).
 */

import type { NeuEintrag } from "./was-ist-neu.js";
import { bauePunkt, punkteVon } from "./neu-punkt.js";

/** Ein Satz der Karte; `neu` = noch nicht angesehen. */
export interface KartenEintrag extends NeuEintrag {
  neu: boolean;
}

export interface KartenOptionen {
  eintraege: KartenEintrag[];
  /** Es gibt mehr Ungesehenes, als die Liste trägt. */
  mehr: boolean;
  /**
   * Die Kurzfassung des Ungesehenen (höchstens 5 Punkte, Server: /api/neuigkeiten/kurz). Da: die Karte zeigt nur sie, die
   * ausführlichen Sätze erst hinter „Details“, und das Vorlesen liest nur sie. Fehlt sie (Notschalter, Ausfall): die Karte
   * wie früher.
   */
  kurz?: string[];
  /**
   * Die KI-Kurzfassung, die gleich nachkommt: die Karte steht SOFORT mit `kurz` (Rückfall) da und tauscht die Punkte aus, sobald
   * sie da ist (null/leer/Fehler: der Rückfall bleibt). Ist das Vorlesen gerade im Gang, bleibt sein Text unberührt.
   */
  kurzNachreichen?: Promise<string[] | null>;
  /** Die Karte war ihre Frist (`sichtFrist`) am Stück im Bild: der Ablauf setzt den Merker und zeichnet den Chip neu. */
  onGesehen: () => void;
  /** „Alles gelesen“ getippt (Martin 17.10.2026): sofort auf 0 — der Ablauf setzt den Merker und zeichnet den Chip neu. */
  onGelesen: () => void;
}

/** Mindestens so lange muss die Karte am Stück im Bild stehen, damit sie als angesehen gilt (ein Vorbeiscrollen zählt nicht). */
export const SICHT_MS = 3000;
/** Dazu je Neuerung: so lange braucht man für Überschrift und Satz. */
export const SICHT_JE_PUNKT_MS = 1500;
/** Länger als das verlangt keine Karte, auch mit sehr vielen Neuerungen. */
export const SICHT_MAX_MS = 6000;

/** Die Frist für eine Karte mit `n` ungesehenen Neuerungen: ca. 1,5 s je Neuerung, mindestens 3 s, höchstens 6 s. */
export const sichtFrist = (n: number): number => Math.min(SICHT_MAX_MS, Math.max(SICHT_MS, n * SICHT_JE_PUNKT_MS));

/**
 * Ruft `onSichtbar` einmal, sobald `karte` SICHT_MS am Stück sichtbar war: Seite im Vordergrund und mindestens die
 * Hälfte der Karte (oder des Bildschirms, bei einer hohen Karte) im Bild. Verlässt sie das Bild oder die Seite,
 * beginnt die Zeit von vorn; wird sie aus dem Verlauf genommen, endet alles. Ohne IntersectionObserver zählt die
 * Zeit, in der die Karte im Dokument steht und die Seite sichtbar ist. Wirft nie.
 */
export function beobachteSicht(karte: HTMLElement, onSichtbar: () => void, ms: number = SICHT_MS): () => void {
  let imBild = typeof IntersectionObserver === "undefined";
  let timer: ReturnType<typeof setTimeout> | null = null;
  let io: IntersectionObserver | null = null;
  const ende = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    io?.disconnect();
    document.removeEventListener("visibilitychange", pruefe);
  };
  function pruefe(): void {
    const an = imBild && document.visibilityState !== "hidden";
    if (an && !timer) {
      timer = setTimeout(() => {
        timer = null;
        if (!karte.isConnected) return ende();
        ende();
        try {
          onSichtbar();
        } catch {
          /* Ansehen darf nie etwas brechen */
        }
      }, ms);
    } else if (!an && timer) {
      clearTimeout(timer);
      timer = null;
    }
  }
  try {
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (eintraege) => {
          const e = eintraege.at(-1);
          if (!e) return;
          const hoehe = e.rootBounds?.height ?? window.innerHeight;
          imBild = e.isIntersecting && (e.intersectionRatio >= 0.5 || e.intersectionRect.height >= hoehe * 0.5);
          pruefe();
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] },
      );
      io.observe(karte);
    }
    document.addEventListener("visibilitychange", pruefe);
    pruefe();
  } catch {
    ende();
  }
  return ende;
}

/** Ende eines Satzes (damit die Stimme zwischen Überschrift und Satz atmet). */
const mitPunkt = (t: string): string => (/[.!?…:]$/u.test(t) ? t : `${t}.`);

/**
 * Der Vorlesetext der Karte: „Das ist neu.“, dann je Neuerung Überschrift und Satz. Nur die Daten, die die Karte
 * ohnehin zeigt — ohne „Wo“, Bild, Version. Mit `mehr` (Karte zeigt „+“) endet er mit „Und weitere kleine Verbesserungen.“ Leer, wenn es nichts Ungesehenes gibt.
 */
export function neuVorlesetext(eintraege: KartenEintrag[], mehr = false): string {
  const teile: string[] = [];
  for (const e of eintraege) {
    if (!e.neu) continue;
    for (const p of punkteVon(e)) {
      const titel = p.titel.trim();
      const text = p.text.trim();
      if (titel) teile.push(mitPunkt(titel));
      if (text) teile.push(mitPunkt(text));
    }
  }
  if (teile.length === 0) return "";
  // „+“ an der Karte: der Server hat mehr, als geliefert wurde.
  if (mehr) teile.push("Und weitere kleine Verbesserungen.");
  return `Das ist neu. ${teile.join(" ")}`;
}

/** Der Vorlesetext der Kurzfassung: „Das ist neu.“, dann die Punkte — nie die ausführlichen Sätze hinter „Details“. Leer ohne Punkte. */
export function neuKurzVorlesetext(kurz: readonly string[]): string {
  const teile = kurz.map((p) => p.trim()).filter(Boolean).map(mitPunkt);
  return teile.length === 0 ? "" : `Das ist neu. ${teile.join(" ")}`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Sätze nach Datum gruppiert; die Reihenfolge der Liste bleibt. */
function gruppiere(eintraege: KartenEintrag[]): Array<{ datum: string; saetze: KartenEintrag[] }> {
  const gruppen: Array<{ datum: string; saetze: KartenEintrag[] }> = [];
  for (const e of eintraege) {
    const g = gruppen.find((x) => x.datum === e.datum);
    if (g) g.saetze.push(e);
    else gruppen.push({ datum: e.datum, saetze: [e] });
  }
  return gruppen;
}

function gruppenZeilen(eintraege: KartenEintrag[], ziel: HTMLElement): void {
  for (const g of gruppiere(eintraege)) {
    if (g.datum) ziel.appendChild(el("div", "neuk-tag", g.datum));
    // Je Neuerung ein Eintrag (Überschrift, Satz, Ort, Bild — lib/neu-punkt.ts); textContent, nie innerHTML.
    for (const e of g.saetze) {
      for (const p of punkteVon(e)) ziel.appendChild(bauePunkt(p, "div", e.neu ? "neuk-satz neuk-satz--neu" : "neuk-satz"));
    }
  }
}

/** Wie viele Neuerungen in diesen Einträgen stehen. */
const zahl = (eintraege: KartenEintrag[]): number => eintraege.reduce((n, e) => n + punkteVon(e).length, 0);

/**
 * Die Karte „Das ist neu“ als Gesprächselement. Ungesehenes steht offen mit Ember-Strich, Älteres hinter
 * „Ältere ansehen“ (<details>, ohne Skript bedienbar). Ist die Karte lange genug im Bild, ruft sie `onGesehen`
 * und nimmt den Strich weg („alles gelesen“).
 */
export function baueNeuKarte(o: KartenOptionen): HTMLElement {
  const karte = el("section", "neuk");
  karte.setAttribute("aria-label", "Neuerungen");

  const kopf = el("div", "neuk-kopf");
  const sub = el("span", "neuk-sub");
  sub.setAttribute("aria-live", "polite");
  // Rechts: Stand und — nur bei Ungesehenem — der Vorlese-Knopf (derselbe wie an jeder Antwort, Platz s. u.).
  const rechts = el("div", "neuk-rechts");
  const vorlesen = el("span", "neuk-vorlesen");
  vorlesen.setAttribute("role", "group");
  vorlesen.setAttribute("aria-label", "Neuerungen vorlesen");
  rechts.append(sub, vorlesen);
  kopf.append(el("h3", "neuk-titel", "Das ist neu"), rechts);
  karte.appendChild(kopf);

  const neue = o.eintraege.filter((e) => e.neu);
  const alte = o.eintraege.filter((e) => !e.neu);

  const oben = el("div", "neuk-neu");
  gruppenZeilen(neue, oben);
  // Mit Kurzfassung: oben nur ihre Punkte; die ausführlichen Sätze stehen dahinter, erst nach dem Tippen auf „Details“.
  const kurz = neue.length > 0 ? (o.kurz ?? []).filter((p) => p.trim()) : [];
  let kurzListe: HTMLElement | null = null;
  if (kurz.length > 0) {
    const liste = el("ul", "neuk-kurz neuk-kurz--neu");
    liste.setAttribute("aria-label", "Kurzfassung");
    liste.replaceChildren(...kurz.map((p) => el("li", "neuk-kurz-punkt", p)));
    kurzListe = liste;
    const details = el("button", "neuk-details-knopf", "Details");
    details.type = "button";
    details.setAttribute("aria-expanded", "false");
    oben.id = `neuk-details-${Math.random().toString(36).slice(2, 8)}`;
    details.setAttribute("aria-controls", oben.id);
    oben.hidden = true;
    details.addEventListener("click", () => {
      const auf = oben.hidden;
      oben.hidden = !auf;
      details.setAttribute("aria-expanded", auf ? "true" : "false");
      details.textContent = auf ? "Details ausblenden" : "Details";
    });
    karte.append(liste, details);
  }
  karte.appendChild(oben);

  if (alte.length > 0) {
    const aelter = document.createElement("details");
    aelter.className = "neuk-aelter";
    // Ist nichts ungesehen, ist das Ältere der Grund für den Tipp: gleich offen.
    aelter.open = neue.length === 0;
    aelter.appendChild(el("summary", "neuk-aelter-kopf", "Ältere ansehen"));
    gruppenZeilen(alte, aelter);
    karte.appendChild(aelter);
  }

  const alle = zahl(o.eintraege);
  const nNeu = zahl(neue);
  const schreibeKopf = (n: number): void => {
    const mehr = n > 0 && o.mehr && n === alle ? "+" : "";
    sub.textContent = n > 0 ? `${n}${mehr} ungesehen` : "alles gelesen";
    karte.toggleAttribute("data-neu", n > 0);
  };
  schreibeKopf(nNeu);

  // Vorlesen: die vorhandene Schiene (voice-v8:vorlese-knopf, app.ts) setzt den Knopf in den Platz — Tippen liest,
  // nochmal Tippen pausiert/setzt fort, genau wie an jeder Antwort. Vorlesen allein macht nichts „gesehen“; das
  // Mikro öffnet es nie (das regelt die gemeinsame Wiedergabe). Ohne Ungesehenes oder ohne Schiene bleibt der Platz leer.
  const meldeVorlesen = (text: string): void => {
    window.dispatchEvent(new CustomEvent("voice-v8:vorlese-knopf", { detail: { text, platz: vorlesen } }));
  };
  const vorlesetext = kurz.length > 0 ? neuKurzVorlesetext(kurz) : neuVorlesetext(o.eintraege, o.mehr && nNeu === alle);
  if (nNeu > 0 && vorlesetext) meldeVorlesen(vorlesetext);

  // Die KI-Fassung kommt nach: Punkte austauschen, Vorlesetext neu setzen — nie mitten im Vorlesen und nie nach „gelesen“.
  if (kurzListe && o.kurzNachreichen) {
    const liste = kurzListe;
    void o.kurzNachreichen
      .then((neuePunkte) => {
        const punkte = (neuePunkte ?? []).map((p) => p.trim()).filter(Boolean);
        if (punkte.length === 0) return;
        liste.replaceChildren(...punkte.map((p) => el("li", "neuk-kurz-punkt", p)));
        const laeuft = vorlesen.querySelector("[data-state]:not([data-state='idle'])") !== null;
        if (karte.hasAttribute("data-neu") && !laeuft) meldeVorlesen(neuKurzVorlesetext(punkte));
      })
      .catch(() => undefined);
  }

  // „Alles gelesen“: ein klarer Knopf, der sofort auf 0 setzt (das bloße Ansehen braucht seine Frist).
  const fuss = el("div", "neuk-fuss");
  const gelesen = el("button", "neuk-knopf", "Alles gelesen");
  gelesen.type = "button";
  gelesen.setAttribute("aria-label", "Alles als gelesen markieren");
  fuss.appendChild(gelesen);
  fuss.hidden = nNeu === 0;
  karte.appendChild(fuss);
  const setzeGelesen = (): void => {
    for (const s of karte.querySelectorAll(".neuk-satz--neu")) s.classList.remove("neuk-satz--neu");
    karte.querySelector(".neuk-kurz--neu")?.classList.remove("neuk-kurz--neu");
    schreibeKopf(0);
    fuss.hidden = true;
  };
  let stopSicht: () => void = () => {};
  gelesen.addEventListener("click", () => {
    stopSicht();
    o.onGelesen();
    setzeGelesen();
    // Der Fokus darf nicht mit dem Knopf verschwinden.
    karte.tabIndex = -1;
    karte.focus({ preventScroll: true });
  });

  // Nichts Ungesehenes: nichts zu merken. Sonst zählt das Ansehen, je länger die Karte, desto länger die Frist.
  if (nNeu > 0) {
    stopSicht = beobachteSicht(
      karte,
      () => {
        o.onGesehen();
        setzeGelesen();
      },
      sichtFrist(nNeu),
    );
  }

  return karte;
}
